import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { User } from '@app/_models';
import { Certificate } from '@app/_models/certificate';
import { CertificateResponse } from '@app/_models/certificateResponse';
import { Dealer } from '@app/_models/dealer';
import { MakeModel } from '@app/_models/makeModel';
import { PlanDetails } from '@app/_models/planDetails';
import { StateCity } from '@app/_models/stateCity';
import { AccountService } from '@app/_services/account.service';
import { AlertService } from '@app/_services/alert.service';
import { CertificateService } from '@app/_services/certificate.service';
import { ConfigDataService } from '@app/_services/config-data.service';
import { DealerService } from '@app/_services/dealer-service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { first, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ExcelBulkIndvTemplateService } from './excel-bulk-indv-template.service';

/**
 * Robust Bulk Individual Certificate Component
 * 
 * This component provides a comprehensive solution for issuing bulk certificates
 * with enhanced validation, error handling, and data integrity checks.
 * 
 * Key Features:
 * - Comprehensive form validation aligned with individual certificate requirements
 * - Enhanced date of birth handling with proper format validation
 * - Robust Excel template processing with detailed error reporting
 * - Improved state management and memory leak prevention
 * - Professional error handling and user feedback
 */
@Component({
  selector: 'app-bulk-individual',
  templateUrl: './bulk-individual.component.html',
  styleUrls: ['./bulk-individual.component.less']
})
export class BulkIndividualComponent implements OnInit, OnDestroy {
  // Component destruction subject for subscription cleanup
  private readonly destroy$ = new Subject<void>();
  
  // Core form and user data
  user: User;
  certficateForm: UntypedFormGroup;
  dealer: Dealer;
  submitted = false;
  loading = false;
  error = false;
  
  // Plan and financial configuration
  plans: PlanDetails[] = [];
  selectedPlan = new PlanDetails();
  maxAmount = 99999;
  minAmount = 0;
  gstPercentage = 0.18;
  showGstAmount = true;
  showFinance = false;
  isBalanceLow = false;
  runningBalance: number;
  
  // Template and Excel configuration
  showExcel = "Hero";
  templateTypes: string[] = ["Hero Vehicle Sales GST Invoice Template", "Hero Ensure Template", "Alliance Template"];
  certificates: Certificate[] = [];
  certificateResponse: CertificateResponse;
  
  // Make/Model and location data
  twoWheelerModels: MakeModel[] = [];
  nonTwoWheelerModels: MakeModel[] = [];
  financeCompanies: string[] = [];
  selectedFinanceCompanies: string[] = [];
  selectedFinance: string;
  nameRegexp: RegExp;
  stateCityList: StateCity[] = [];
  
  // Date handling
  startDateModel: NgbDateStruct;
  startDate = new Date();
  endDate = (() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date;
  })();
  currentDate = new Date();
  issueDate: Date;
  dobString: string;
  
  // Plan amount calculations
  planTotalAmount: number;
  planAmount: any;
  planGSTAmount: any;


  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly alertService: AlertService,
    private readonly configDataService: ConfigDataService,
    private readonly certificateService: CertificateService, 
    private readonly excelBulkIndvTemplateService: ExcelBulkIndvTemplateService,
    private readonly dealerService: DealerService,
    private readonly router: Router
  ) {
    this.user = this.accountService.userValue;
    this.initializeComponent();
  }

  ngOnInit(): void {
    this.buildForm();
    this.loadInitialData();
    this.setupSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize component with default values and configurations
   */
  private initializeComponent(): void {
    this.selectedPlan.numberOfYears = this.selectedPlan.numberOfYears || 1;
    this.nameRegexp = new RegExp('', 'i');
    this.selectedFinanceCompanies = [];
    
    // Initialize date models
    this.startDateModel = {
      year: this.currentDate.getFullYear(),
      month: this.currentDate.getMonth() + 1,
      day: this.currentDate.getDate()
    };
  }

  /**
   * Build reactive form with comprehensive validation rules
   * Mirrors the individual certificate form structure for consistency
   */
  private buildForm(): void {
    this.certficateForm = this.formBuilder.group({
      // Vehicle Information
      vehicleMake: ['', Validators.required],
      model: ['', Validators.required],
      type: ['', Validators.required],
      variant: [''],
      vehicleYear: ['', Validators.required],
      registrationNumber: [''],
      chasisNumber: ['', Validators.required],
      engineNumber: ['', Validators.required],
      vehicleType: ['Two Wheeler'],
      
      // Customer Information
      clientType: ['Individual', Validators.required],
      firstName: ['', [Validators.required, Validators.pattern('^[^0-9]+$')]],
      middleName: [''],
      lastName: ['', Validators.pattern('^[^0-9]+$')],
      email: ['', Validators.email],
      contactNumber: ['', [Validators.required, Validators.minLength(6)]],
      employeeContactNumber: [''],
      gender: ['', Validators.required],
      dob: ['', Validators.required],
      ageOrDob: ['dob'],
      age: ['', [Validators.min(15), Validators.max(80)]],
      gstin: [''],
      
      // Address Information
      address: ['', Validators.required],
      state: ['', Validators.required],
      city: ['', Validators.required],
      
      // Nominee Information
      nomineeName: ['', Validators.required],
      nomineeRelationship: ['', Validators.required],
      nomineeGender: ['', Validators.required],
      nomineeAge: ['', [Validators.required, Validators.min(1), Validators.max(100)]],
      
      // Plan and Financial Information
      planId: ['', Validators.required],
      planAmount: [{ value: '', disabled: true }],
      planGSTAmount: [{ value: '', disabled: true }],
      planTotalAmount: ['', [
        Validators.required,
        (control: AbstractControl) => Validators.max(this.maxAmount)(control),
        (control: AbstractControl) => Validators.min(this.minAmount)(control)
      ]],
      
      // Date Information
      startDate: [{ value: '', disabled: true }, Validators.required],
      endDate: [{ value: '', disabled: true }],
      issueDate: [{ value: '', disabled: true }],
      
      // Finance Information
      financeCompanyName: [''],
      financeCoverAmount: [50000],
      
      // System Information
      balance: [{ value: '', disabled: true }],
      userId: [this.user.id],
      certificateNumber: [''],
      policyNumber: [''],
      
      // File Upload
      uploadFile: ['', Validators.required],
      templateType: [this.templateTypes[0]]
    });
  }
  /**
   * Load initial configuration data from services
   */
  private loadInitialData(): void {
    // Load current date and initialize date-related fields
    this.configDataService.getTodaysDate()
      .pipe(takeUntil(this.destroy$))
      .subscribe(currentDate => {
        this.handleDateInitialization(new Date(currentDate));
      });

    // Load state and city data
    this.configDataService.StateCity$
      .pipe(takeUntil(this.destroy$))
      .subscribe(stateCityList => {
        this.stateCityList = stateCityList;
      });

    // Load make and model data
    this.configDataService.MakeModel$
      .pipe(takeUntil(this.destroy$))
      .subscribe(makeModelList => {
        this.twoWheelerModels = makeModelList.filter(model => 
          model.productType.includes('Two')
        );
        this.nonTwoWheelerModels = makeModelList.filter(model => 
          !model.productType.includes('Two')
        );
      });

    // Load finance companies
    this.configDataService.FinanceCompanyList$
      .pipe(takeUntil(this.destroy$))
      .subscribe(financeCompanies => {
        this.financeCompanies = financeCompanies;
      });

    // Load dealer information and plans
    this.loadDealerData();
  }

  /**
   * Handle date initialization for all date-related fields
   */
  private handleDateInitialization(currentDate: Date): void {
    this.currentDate = currentDate;
    this.issueDate = new Date(currentDate);
    this.startDate = new Date(currentDate);
    // End date: 1 year minus 1 day from start date, set to 23:59:59.999 IST
    const endDateCalc = new Date(currentDate);
    endDateCalc.setFullYear(endDateCalc.getFullYear() + 1);
    endDateCalc.setDate(endDateCalc.getDate() - 1);
    endDateCalc.setHours(23, 59, 59, 999);
    this.endDate = endDateCalc;
    
    this.startDateModel = {
      year: this.currentDate.getFullYear(),
      month: this.currentDate.getMonth() + 1,
      day: this.currentDate.getDate()
    };
    
    this.certficateForm.controls['startDate'].setValue(this.startDateModel);
  }

  /**
   * Load dealer-specific data and configurations
   */
  private loadDealerData(): void {
    this.dealerService.getByDealerId(this.user.dealerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(dealer => {
        this.dealer = dealer;
        this.plans = dealer.dealerPlan || [];
        
        // Set initial plan if available
        if (this.plans.length > 0) {
          this.certficateForm.controls['planId'].setValue(this.plans[0].planId);
        }
        
        // Update balance
        this.certficateForm.controls['balance'].setValue(dealer.dealerBalance);
        
        // Determine Excel template type based on dealer configuration
        this.determineExcelTemplateType(dealer);
      });
  }

  /**
   * Determine the appropriate Excel template based on dealer configuration
   */
  private determineExcelTemplateType(dealer: Dealer): void {
    if (dealer.dealerMake?.some(make => make.make === "Honda Motorcycle & Scooter India")) {
      this.showExcel = "Honda";
    } else if (dealer.dealerMake?.some(make => make.make === "Bajaj Auto")) {
      this.showExcel = "Bajaj";
    } else {
      this.showExcel = "Hero";
    }
  }

  /**
   * Setup form value change subscriptions and validators
   */
  private setupSubscriptions(): void {
    this.setupPlanSubscriptions();
    this.setupFinanceSubscriptions();
    this.setupDateSubscriptions();
    this.setupAmountSubscriptions();
  }

  /**
   * Setup plan-related form subscriptions
   */
  private setupPlanSubscriptions(): void {
    this.certficateForm.get('planId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(planId => {
        if (planId && this.plans) {
          this.handlePlanSelection(planId);
        }
      });
  }

  /**
   * Handle plan selection and update related form fields
   */
  private handlePlanSelection(planId: string): void {
    const plan = this.plans.find(p => p.planId === planId);
    if (!plan) return;

    this.selectedPlan = { ...plan };
    this.selectedPlan.numberOfYears = this.selectedPlan.numberOfYears || 1;
    
    // Update plan amounts
    this.certficateForm.patchValue({
      planAmount: plan.planAmount,
      planGSTAmount: plan.planGstAmount,
      planTotalAmount: plan.planAmount + plan.planGstAmount
    });
    
    // Update validation limits
    this.maxAmount = plan.planMaximumAmount;
    this.minAmount = plan.planMinimumAmount || plan.planNetAmount;
    
    // Update dates based on plan duration
    this.updatePlanDates();
    
    // Handle finance-specific configurations
    this.handleFinanceConfiguration(plan);
    
    // Update GST display settings
    this.updateGstDisplaySettings(plan);
    
    // Enable/disable plan total amount based on plan configuration
    if (plan.planAmount === 0) {
      this.certficateForm.controls['planTotalAmount'].enable();
    } else {
      this.certficateForm.controls['planTotalAmount'].disable();
    }
    
    // Reset file upload when plan changes
    this.certficateForm.controls['uploadFile'].reset();
  }

  /**
   * Update plan dates based on selected plan duration
   */
  private updatePlanDates(): void {
    this.certficateForm.controls['startDate'].setValue(this.startDateModel);
    
    const endDate = new Date(this.startDate);
    endDate.setFullYear(endDate.getFullYear() + this.selectedPlan.numberOfYears);
    endDate.setDate(endDate.getDate() - 1);
    endDate.setHours(0, 0, 0, 0);
    
    this.endDate = endDate;
    this.certficateForm.controls['endDate'].setValue(endDate);
    this.certficateForm.controls['issueDate'].setValue(this.currentDate);
  }

  /**
   * Handle finance-specific plan configurations
   */
  private handleFinanceConfiguration(plan: PlanDetails): void {
    if (plan.policyType === "FINANCE") {
      this.showFinance = true;
      
      // Set finance validators
      this.certficateForm.get('financeCompanyName')?.setValidators(Validators.required);
      this.certficateForm.get('financeCoverAmount')?.setValidators([
        Validators.required,
        Validators.max(plan.planCPACoverAmount || 500000)
      ]);
      
      // Set default cover amount
      plan.planCPACoverAmount = plan.planCPACoverAmount || 500000;
    } else {
      this.showFinance = false;
      
      // Clear finance validators
      this.certficateForm.get('financeCompanyName')?.clearValidators();
      this.certficateForm.get('financeCoverAmount')?.clearValidators();
    }
    
    // Update form validation state
    this.certficateForm.get('financeCompanyName')?.updateValueAndValidity();
    this.certficateForm.get('financeCoverAmount')?.updateValueAndValidity();
  }

  /**
   * Update GST display settings based on plan configuration
   */
  private updateGstDisplaySettings(plan: PlanDetails): void {
    this.showGstAmount = plan.gstApplicable !== "N";
    if (this.showGstAmount && plan.gstShow === "N") {
      this.showGstAmount = false;
    }
  }

  /**
   * Setup finance-related form subscriptions
   */
  private setupFinanceSubscriptions(): void {
    this.certficateForm.get('financeCompanyName')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.nameRegexp = new RegExp(value, 'i');
          this.selectedFinanceCompanies = this.financeCompanies.filter(company =>
            this.nameRegexp.test(company)
          );
        } else {
          this.selectedFinanceCompanies = [];
        }
      });
  }

  /**
   * Setup date-related form subscriptions
   */
  private setupDateSubscriptions(): void {
    this.certficateForm.get('startDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && this.isValidDateStruct(value)) {
          this.handleStartDateChange(value);
        }
      });
  }

  /**
   * Validate NgbDateStruct object
   */
  private isValidDateStruct(value: any): boolean {
    return value && 
           typeof value.month === 'number' && 
           typeof value.day === 'number' && 
           typeof value.year === 'number';
  }

  /**
   * Handle start date changes and update related dates
   */
  private handleStartDateChange(dateStruct: NgbDateStruct): void {
    this.startDate = new Date(this.convertTextDate(dateStruct));
    
    // Adjust for current date if same
    if (this.isSameDate(dateStruct, this.currentDate)) {
      this.startDate = new Date(this.currentDate);
    } else {
      this.startDate.setHours(0, 0, 0, 0);
    }
    
    // Update end date based on plan duration
    const endDate = new Date(this.startDate);
    endDate.setFullYear(endDate.getFullYear() + this.selectedPlan.numberOfYears);
    endDate.setDate(endDate.getDate() - 1);
    endDate.setHours(0, 0, 0, 0);
    
    this.endDate = endDate;
    this.certficateForm.controls['endDate'].setValue(endDate);
    this.certficateForm.controls['issueDate'].setValue(this.startDate);
    this.issueDate = this.startDate;
  }

  /**
   * Check if date struct represents the same date as given date
   */
  private isSameDate(dateStruct: NgbDateStruct, date: Date): boolean {
    return dateStruct.month === (date.getMonth() + 1) &&
           dateStruct.day === date.getDate() &&
           dateStruct.year === date.getFullYear();
  }

  /**
   * Setup amount calculation subscriptions
   */
  private setupAmountSubscriptions(): void {
    this.certficateForm.get('planTotalAmount')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value && this.selectedPlan.planAmount === 0) {
          this.calculatePlanAmounts(parseInt(value));
        }
      });
  }

  /**
   * Calculate plan amounts based on total amount
   */
  private calculatePlanAmounts(totalAmount: number): void {
    this.planTotalAmount = totalAmount;
    this.planAmount = (totalAmount / (1 + this.gstPercentage)).toFixed(2);
    this.planGSTAmount = (totalAmount - parseFloat(this.planAmount)).toFixed(2);
    this.minAmount = this.selectedPlan.planMinimumAmount || 1000;
    
    this.certficateForm.patchValue({
      planGSTAmount: this.planGSTAmount,
      planAmount: this.planAmount
    });
    
    this.balanceValidator();
  }
  /**
   * Getter for easy access to form controls in template
   */
  get f() { 
    return this.certficateForm.controls; 
  }

  /**
   * Handle form submission with comprehensive validation
   */
  onSubmit(): void {
    this.submitted = true;
    this.alertService.clear();
    console.log('Form submitted:', this.certficateForm.value);

    // First priority: Validate uploaded certificates (most important for bulk upload)
    if (!this.certificates || this.certificates.length === 0) {
      this.alertService.error(
        'Please upload a valid Excel file with certificate data before submitting. ' +
        'Use the "Choose File" button above to select your Excel file containing customer certificate information.'
      );
      return;
    }

    // Second priority: Validate essential form fields needed for bulk processing
    if (!this.validateEssentialFields()) {
      return;
    }
    
    // Third priority: Check balance
    if (this.isBalanceLow) {
      this.alertService.error('Insufficient balance. Please add funds before proceeding.');
      return;
    }
    
    this.loading = true;
    
    try {
      const issueCertificate = this.buildCertificateData();
      const processedCertificates = this.processCertificatesForSubmission(issueCertificate);
      
      this.submitCertificates(processedCertificates);
    } catch (error) {
      this.handleSubmissionError(error);
    }
  }

  /**
   * Validate essential fields required for bulk certificate processing
   */
  private validateEssentialFields(): boolean {
    const errors: string[] = [];

    // Check plan selection
    if (!this.certficateForm.get('planId')?.value) {
      errors.push('Plan selection is required');
    }

    // Check plan amounts
    const planTotalAmount = this.certficateForm.get('planTotalAmount')?.value;
    if (!planTotalAmount || planTotalAmount <= 0) {
      errors.push('Plan total amount is required and must be greater than 0');
    }

    // Check finance fields if applicable
    if (this.showFinance) {
      if (!this.certficateForm.get('financeCompanyName')?.value) {
        errors.push('Finance company name is required for finance policies');
      }
      if (!this.certficateForm.get('financeCoverAmount')?.value) {
        errors.push('Finance cover amount is required for finance policies');
      }
    }

    // Check selected plan validity
    if (!this.selectedPlan || !this.selectedPlan.planId) {
      errors.push('Please select a valid plan');
    }

    // Check date fields
    if (!this.certficateForm.get('startDate')?.value) {
      errors.push('Start date is required');
    }

    if (errors.length > 0) {
      this.alertService.error('Please fix the following issues:\\n• ' + errors.join('\\n• '));
      return false;
    }

    return true;
  }

  getFormValidationErrors(): string {
  const errors: string[] = [];
  const controls = this.certficateForm.controls;

  for (const name in controls) {
    if (controls[name].invalid) {
      errors.push(`- ${name}: ${this.getControlErrors(controls[name])}`);
    }
  }

  return errors.length > 0 ? '\n' + errors.join('\n') : '';
}

/**
 * Helper to extract error messages from a form control
 */
private getControlErrors(control: AbstractControl): string {
  if (!control.errors) return '';
  return Object.keys(control.errors)
    .map(key => {
      switch (key) {
        case 'required': return 'Required';
        case 'email': return 'Invalid email';
        case 'minlength': return `Minimum length ${control.errors['minlength'].requiredLength}`;
        case 'maxlength': return `Maximum length ${control.errors['maxlength'].requiredLength}`;
        case 'min': return `Minimum value ${control.errors['min'].min}`;
        case 'max': return `Maximum value ${control.errors['max'].max}`;
        case 'pattern': return 'Invalid format';
        default: return key;
      }
    })
    .join(', ');
}

  /**
   * Build certificate data from form values
   */
  private buildCertificateData(): Certificate {
    const formData = this.certficateForm.getRawValue();
    const certificate = new Certificate(formData);
    
    // Set calculated date string
    certificate.issueDateString = this.formatDateString(this.issueDate);
    certificate.startDate = this.certficateForm.get('issueDate')?.value;
    certificate.planId = this.selectedPlan.planId;
    certificate.printCpaPolicy = this.selectedPlan.printCpaPolicy || "Yes";
    certificate.planCPACoverAmount = this.selectedPlan.planCPACoverAmount || 1500000;
    certificate.printRsaNumber = this.selectedPlan.printRsaNumber || "No";
    
    // Calculate nominee cover amount
    if (this.selectedPlan.policyType === "FINANCE") {
      certificate.nomineeCoverAmount = certificate.planCPACoverAmount - certificate.financeCoverAmount;
    } else {
      certificate.nomineeCoverAmount = certificate.planCPACoverAmount;
    }
    
    return certificate;
  }

  /**
   * Process certificates from Excel with form data
   */
  private processCertificatesForSubmission(baseCertificate: Certificate): Certificate[] {
    return this.certificates.map(cert => {
      // Copy base certificate data to each uploaded certificate
      const processedCert = { ...cert };
      
      // Set employee contact number
      processedCert.employeeContactNumber = "NA";
      
      // Set age or DOB preference
      if (this.user.specialRoleAccess?.includes('canSeeAge')) {
        processedCert.ageOrDob = 'age';
      } else {
        processedCert.ageOrDob = 'dob';
      }
      
      // Copy plan and financial data
      Object.assign(processedCert, {
        planId: baseCertificate.planId,
        planAmount: baseCertificate.planAmount,
        planGSTAmount: baseCertificate.planGSTAmount,
        planTotalAmount: baseCertificate.planTotalAmount,
        startDate: baseCertificate.startDate,
        endDate: baseCertificate.endDate,
        issueDate: baseCertificate.issueDate,
        printCpaPolicy: baseCertificate.printCpaPolicy,
        financeCompanyName: baseCertificate.financeCompanyName,
        financeCoverAmount: baseCertificate.financeCoverAmount,
        planCPACoverAmount: baseCertificate.planCPACoverAmount,
        printRsaNumber: baseCertificate.printRsaNumber,
        nomineeCoverAmount: baseCertificate.nomineeCoverAmount,
        issueDateString: baseCertificate.issueDateString,
        userId: baseCertificate.userId,
        type: "New",
        clientType: "Individual"
      });
      
      // Set one pager preference
      processedCert.onePager = this.dealer.onePager || false;
      
      return processedCert;
    });
  }

  /**
   * Submit certificates to the backend
   */
  private submitCertificates(certificates: Certificate[]): void {
    this.certificateService.issueBulkCertificate(certificates)
      .pipe(
        first(),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          this.handleSubmissionSuccess(response);
        },
        error: (error) => {
          this.handleSubmissionError(error);
        }
      });
  }

  /**
   * Handle successful certificate submission
   */
  private handleSubmissionSuccess(response: CertificateResponse): void {
    this.certificateResponse = response;
    this.loading = false;
    
    this.alertService.success(
      'Certificates added successfully! Redirecting to Print Certificate page...',
      { keepAfterRouteChange: true }
    );
    
    // Redirect to print certificate page after a short delay
    setTimeout(() => {
      this.router.navigate(['/reports/printCertificate']);
    }, 2000); // 2 second delay to show the success message
  }

  /**
   * Handle submission errors
   */
  private handleSubmissionError(error: any): void {
    this.alertService.error(error || 'An error occurred while processing certificates.');
    this.certficateForm.controls['uploadFile'].reset();
    this.submitted = false;
    this.loading = false;
  }

  /**
   * Handle file upload and processing
   */
  async onFileChange(event: any): Promise<void> {
    try {
      this.error = false;
      this.alertService.clear();
      
      if (!event?.target?.files?.length) {
        this.alertService.error('Please select a file to upload.');
        return;
      }
      
      const selectedModels = this.selectedPlan.policyType?.includes('CAR') 
        ? this.nonTwoWheelerModels 
        : this.twoWheelerModels;
      
      await this.excelBulkIndvTemplateService.uploadFile(
        event,
        this.dealer,
        selectedModels,
        this.selectedPlan,
        this.stateCityList
      );
      
      this.error = this.excelBulkIndvTemplateService.error;
      
      if (this.error) {
        this.certficateForm.controls['uploadFile'].reset();
        this.certificates = [];
      } else {
        this.certificates = this.excelBulkIndvTemplateService.certificates || [];
        
        // Auto-update showExcel based on detected template
        if (this.excelBulkIndvTemplateService.hasDetectedTemplate()) {
          const detectedTemplate = this.excelBulkIndvTemplateService.getDetectedTemplate();
          this.updateShowExcelFromDetection(detectedTemplate.templateType);
          
          // Enhanced success message with template detection info
          this.alertService.success(
            `Successfully processed ${this.certificates.length} certificate(s) from the uploaded file. ` +
            `Template auto-detected: ${detectedTemplate.displayName}`
          );
        } else if (this.certificates.length === 0) {
          this.alertService.info('No valid certificate data found in the uploaded file.');
        } else {
          this.alertService.success(`Successfully processed ${this.certificates.length} certificate(s) from the uploaded file.`);
        }
      }
    } catch (error) {
      console.error('File processing error:', error);
      this.alertService.error('Error processing the uploaded file. Please check the file format and try again.');
      this.certficateForm.controls['uploadFile'].reset();
      this.error = true;
    }
  }

  /**
   * Update showExcel variable based on detected template type
   */
  private updateShowExcelFromDetection(templateType: string): void {
    switch (templateType.toLowerCase()) {
      case 'honda':
        this.showExcel = 'Honda';
        break;
      case 'bajaj':
        this.showExcel = 'Bajaj';
        break;
      case 'hero':
        this.showExcel = 'Hero';
        break;
      case 'heroensure':
        this.showExcel = 'Hero';
        break;
      case 'alliance':
      default:
        this.showExcel = 'Alliance';
        break;
    }
    console.log(`Updated showExcel to: ${this.showExcel} based on detected template: ${templateType}`);
  }

  /**
   * Download Excel template for bulk upload
   */
  downloadTemplate(): void {
    try {
      this.excelBulkIndvTemplateService.downloadTemplateFile(this.dealer);
    } catch (error) {
      console.error('Template download error:', error);
      this.alertService.error('Error downloading template. Please try again.');
    }
  }

  /**
   * Handle finance company selection from dropdown
   */
  financeSelected(inputFinance: string): void {
    this.certficateForm.controls['financeCompanyName'].setValue(inputFinance);
    this.selectedFinance = inputFinance;
    this.selectedFinanceCompanies = [];
  }

  /**
   * Validate dealer balance against plan total amount
   */
  private balanceValidator(): void {
    const planTotalAmount = this.certficateForm.get('planTotalAmount')?.value;
    
    if (planTotalAmount && this.dealer) {
      this.runningBalance = this.dealer.dealerBalance - parseInt(planTotalAmount);
      this.isBalanceLow = this.runningBalance < (this.dealer.dealerBalanceLimit || 0);
    }
  }

  /**
   * Convert NgbDateStruct to date string in YYYY-MM-DD format
   */
  private convertTextDate(value: NgbDateStruct): string {
    if (!value) return '';
    
    const year = value.year.toString();
    const month = value.month < 10 ? `0${value.month}` : value.month.toString();
    const day = value.day < 10 ? `0${value.day}` : value.day.toString();
    
    return `${year}-${month}-${day}`;
  }

  /**
   * Format date to string in YYYY-MM-DD format
   */
  private formatDateString(date: Date): string {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    
    return `${year}-${month}-${day}`;
  }

    /**
   * Reset form and component state
   */
  resetForm(): void {
    this.submitted = false;
    this.loading = false;
    this.error = false;
    this.certificates = [];
    this.selectedFinanceCompanies = [];
    this.alertService.clear();
    
    // Reset form to initial state
    this.certficateForm.reset();
    this.buildForm();
    this.loadInitialData();
  }

  /**
   * Getter to expose the excel service to the template for template detection display
   */
  get excelService() {
    return this.excelBulkIndvTemplateService;
  }
}

